import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Tabs,
  TabsList,
  TabsTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { ArrowUpDownIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SortChipOption } from '@/shared/ui';
import { SearchInput } from '@/shared/ui/SearchInput';
import type { TrackerOwnership } from '../../lib/filterTrackers';

/** The radio value standing for "no sort picked"; Radix needs a string. */
const DEFAULT_SORT = '';

const OWNERSHIP_OPTIONS = [
  { value: 'all', labelKey: 'trackers.ownershipFilter.all' },
  { value: 'mine', labelKey: 'trackers.ownershipFilter.createdByMe' },
  { value: 'shared', labelKey: 'trackers.ownershipFilter.sharedWithMe' },
] as const satisfies ReadonlyArray<{ value: TrackerOwnership; labelKey: string }>;

export function TrackersToolbar(props: {
  search: string;
  onSearchChange: (value: string) => void;
  sortOptions: ReadonlyArray<SortChipOption>;
  sortId: string | null;
  onSortChange: (id: string | null) => void;
  ownership: TrackerOwnership;
  onOwnershipChange: (ownership: TrackerOwnership) => void;
  onNewTracker: () => void;
}) {
  const { t } = useTranslation('trackers');
  const { t: tCommon } = useTranslation('common');

  return (
    <div
      data-testid="trackers-toolbar"
      className="flex flex-wrap items-center justify-between gap-md p-section"
    >
      <div className="flex flex-wrap items-center gap-2">
        <SearchInput
          value={props.search}
          onChange={props.onSearchChange}
          placeholder={t('trackers.searchTrackers')}
          aria-label={t('trackers.searchTrackersLabel')}
          size="lg"
          className="w-80"
        />

        <TooltipProvider>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon-lg" aria-label={t('trackers.sortTrackers')}>
                    <ArrowUpDownIcon className="size-4" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('trackers.sortTrackers')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuRadioGroup
                value={props.sortId ?? DEFAULT_SORT}
                onValueChange={(value) => props.onSortChange(value === DEFAULT_SORT ? null : value)}
              >
                <DropdownMenuRadioItem value={DEFAULT_SORT}>
                  {tCommon('ui.sortChipPopover.default')}
                </DropdownMenuRadioItem>
                {props.sortOptions.map((option) => (
                  <DropdownMenuRadioItem key={option.id} value={option.id}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
        </TooltipProvider>

        <Tabs
          value={props.ownership}
          onValueChange={(value) => props.onOwnershipChange(value as TrackerOwnership)}
        >
          <TabsList aria-label={t('trackers.ownershipFilter.label')}>
            {OWNERSHIP_OPTIONS.map(({ value, labelKey }) => (
              <TabsTrigger key={value} value={value}>
                {t(labelKey)}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      <Button size="lg" onClick={props.onNewTracker}>
        <PlusIcon className="size-4" />
        {t('trackers.newTracker')}
      </Button>
    </div>
  );
}
